import { QuizTopicPageTemplate } from "@/components/QuizTopicPageTemplate";

const tocItems = [
  { id: "why-art-quizzes", title: "Why Art Quizzes?" },
  { id: "famous-paintings", title: "Famous Paintings Quizzes" },
  { id: "artist-recognition", title: "Artist Recognition" },
  { id: "art-history", title: "Art History Challenges" },
];

const quizResources = [
  { name: "Ultimate Art Quiz", url: "https://www.britannica.com/quiz/ultimate-art-quiz", description: "Ultimate art knowledge test" },
  { name: "Name that Painter!", url: "https://www.britannica.com/quiz/name-that-painter", description: "Match paintings to artists" },
  { name: "Name 50 Famous Paintings", url: "https://play.howstuffworks.com/quiz/only-1-out-60-people-can-identify-the-names-these-50-famous-paintings-can-you", description: "Can you identify them all?" },
  { name: "Art at Random Quiz", url: "https://www.britannica.com/quiz/art-at-random-quiz", description: "Random art trivia" },
  { name: "Almost-Forgotten Artists", url: "https://www.britannica.com/quiz/almost-forgotten-artists-quiz", description: "Test your knowledge of rediscovered artists" },
];

const relatedTopics = [
  { name: "History Quizzes", path: "/quizzes/history-quizzes/", description: "Historical events and figures" },
  { name: "Trivia Quizzes", path: "/quizzes/trivia-quizzes/", description: "General knowledge tests" },
  { name: "Music Quizzes", path: "/quizzes/music-quizzes/", description: "Test your music knowledge" },
];

const ArtQuizzes = () => {
  return (
    <QuizTopicPageTemplate
      title="Art Quizzes: Test Your Art Knowledge"
      metaTitle="Art Quizzes | Famous Paintings & Artist Trivia 2025"
      metaDescription="Test your art knowledge with quizzes on famous paintings, artists, and art history. From the Mona Lisa to modern art movements - how much do you know?"
      keywords="art quizzes, famous paintings quiz, artist trivia, art history quiz, painting recognition, art knowledge test"
      canonicalUrl="/quizzes/art-quizzes/"
      category="Arts & Culture"
      publishDate="January 12, 2025"
      readTime="5 min read"
      tocItems={tocItems}
      leadParagraph="From the Mona Lisa to Starry Night, art has shaped human culture for centuries. Art quizzes let you explore famous paintings, learn about legendary artists, and discover art movements—all while testing how much you really know about the art world."
      quizResources={quizResources}
      relatedTopics={relatedTopics}
    >
      <section id="why-art-quizzes" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Why <span className="text-gradient">Art Quizzes?</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Art appreciation isn't just for museums. Whether you're an art student, a casual appreciator, or just curious, art quizzes offer an engaging way to learn about the masterpieces that have defined human creativity.
        </p>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Art quizzes help you:
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Recognize masterpieces:</strong> Know famous works when you see them</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Learn artist styles:</strong> Distinguish Van Gogh from Monet</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Understand movements:</strong> Renaissance, Impressionism, Cubism, and more</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Appreciate context:</strong> Know the stories behind famous works</span>
          </li>
        </ul>
      </section>

      <section id="famous-paintings" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Famous <span className="text-gradient">Paintings Quizzes</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Can you identify "The Scream" by Edvard Munch? What about "Girl with a Pearl Earring" by Vermeer? <a href="https://play.howstuffworks.com/quiz/only-1-out-60-people-can-identify-the-names-these-50-famous-paintings-can-you" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Only 1 in 60 people can name all 50 famous paintings</a> in this challenging quiz. The <a href="https://www.britannica.com/quiz/ultimate-art-quiz" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Ultimate Art Quiz</a> from Britannica is another great challenge.
        </p>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Popular quiz formats include:
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span>Name the painting from an image</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span>Match the painting to the artist</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span>Guess the era or movement</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span>Identify details and symbols</span>
          </li>
        </ul>
      </section>

      <section id="artist-recognition" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Artist <span className="text-gradient">Recognition</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Each artist has a unique style. Britannica's <a href="https://www.britannica.com/quiz/name-that-painter" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Name that Painter! Quiz</a> tests whether you can identify artists like:
        </p>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Vincent van Gogh</h4>
            <p className="text-sm text-muted-foreground">Bold brushstrokes and emotional intensity</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Claude Monet</h4>
            <p className="text-sm text-muted-foreground">Impressionist master of light</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Pablo Picasso</h4>
            <p className="text-sm text-muted-foreground">Cubism and abstract forms</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Leonardo da Vinci</h4>
            <p className="text-sm text-muted-foreground">Renaissance genius and inventor</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Frida Kahlo</h4>
            <p className="text-sm text-muted-foreground">Personal symbolism and surrealism</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Salvador Dalí</h4>
            <p className="text-sm text-muted-foreground">Surrealist dreamscapes</p>
          </div>
        </div>
      </section>

      <section id="art-history" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Art History <span className="text-gradient">Challenges</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          For those who want to go deeper, art history quizzes cover movements, techniques, and the evolution of artistic expression through the ages. Try the <a href="https://www.britannica.com/quiz/everything-in-art-and-design-part-two-quiz" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Everything in Art and Design Quiz</a>.
        </p>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Test your knowledge of:
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Art movements:</strong> From Baroque to Pop Art</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Techniques:</strong> Chiaroscuro, pointillism, impasto</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Art theft:</strong> Famous stolen paintings and their stories</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Record sales:</strong> The most expensive paintings ever sold</span>
          </li>
        </ul>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Britannica's <a href="https://www.britannica.com/quiz/which-came-first-art-edition" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Which Came First: Art Edition</a> quiz challenges you to determine which masterpieces were created earlier—testing both your art knowledge and sense of historical timeline. For rediscovered artists, try the <a href="https://www.britannica.com/quiz/almost-forgotten-artists-quiz" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Almost-Forgotten Artists Quiz</a>.
        </p>
      </section>
    </QuizTopicPageTemplate>
  );
};

export default ArtQuizzes;
